import { Transaction } from '../transaction/index.js';
import { Beef } from '../transaction/Beef.js';
import OverlayAdminTokenTemplate from './OverlayAdminTokenTemplate.js';
import * as Utils from '../primitives/utils.js';
import { getOverlayHostReputationTracker, HostReputationTracker } from './HostReputationTracker.js';
import { Telemetry } from '../telemetry/Telemetry.js';
const defaultFetch = typeof globalThis !== 'undefined' && typeof globalThis.fetch === 'function'
    ? globalThis.fetch.bind(globalThis)
    : fetch;
/** Default SLAP trackers */
export const DEFAULT_SLAP_TRACKERS = [
    // BSVA clusters
    'https://overlay-us-1.bsvb.tech',
    'https://overlay-eu-1.bsvb.tech',
    'https://overlay-ap-1.bsvb.tech',
    // Babbage primary overlay service
    'https://users.bapp.dev'
    // NOTE: Other entities may submit pull requests to the library if they maintain SLAP overlay services.
    // Additional trackers run by different entities contribute to greater network resiliency.
    // It also generally doesn't hurt to have more trackers in this list.
    // DISCLAIMER:
    // Trackers known to host invalid or illegal records will be removed at the discretion of the BSV Association.
];
/** Default testnet SLAP trackers */
export const DEFAULT_TESTNET_SLAP_TRACKERS = [
    // Babbage primary testnet overlay service
    'https://testnet-users.bapp.dev'
];
/** Default TerraTestNet SLAP trackers. */
export const DEFAULT_TTN_SLAP_TRACKERS = [
    // Canonical staging root; kept separate from testnet to prevent cross-chain discovery.
    'https://staging-overlay.babbage.systems'
];
const MAX_TRACKER_WAIT_TIME = 5000;
const DEFAULT_LOOKUP_TIMEOUT = 2000;
const DEFAULT_UNREACHABLE_NOTIFICATION_COOLDOWN_MS = 60_000;
const MAX_NOTIFICATION_DEDUP_ENTRIES = 512;
/** An HTTP failure with enough classification for reputation handling. */
export class LookupHTTPError extends Error {
    status;
    kind;
    constructor(status, kind, statusText) {
        const detail = typeof statusText === 'string' && statusText.trim().length > 0 ? ` ${statusText.trim()}` : '';
        super(`Failed to facilitate lookup (HTTP ${status}${detail})`);
        this.name = 'LookupHTTPError';
        this.status = status;
        this.kind = kind;
    }
}
/** True when an HTTP response rejects this query without proving host outage. */
function isSemanticLookupRejection(err) {
    return err instanceof LookupHTTPError && err.kind === 'semantic';
}
function lookupErrorMessage(error) {
    if (error instanceof Error)
        return error.message;
    return Reflect.apply(String, undefined, [error]);
}
function isByteArray(value) {
    return (Array.isArray(value) && value.every(byte => Number.isInteger(byte) && byte >= 0 && byte <= 255));
}
function isLookupOutput(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const output = value;
    if (!isByteArray(output.beef) || output.beef.length === 0)
        return false;
    if (!Number.isInteger(output.outputIndex) || output.outputIndex < 0)
        return false;
    if (output.context !== undefined && !isByteArray(output.context))
        return false;
    if (output.txid !== undefined &&
        (typeof output.txid !== 'string' || !/^[0-9a-fA-F]{64}$/.test(output.txid)))
        return false;
    return true;
}
function isOutputListAnswer(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const answer = value;
    return (answer.type === 'output-list' &&
        Array.isArray(answer.outputs) &&
        answer.outputs.every(isLookupOutput));
}
function isFreeformAnswer(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const answer = value;
    return answer.type === 'freeform' && Object.hasOwn(answer, 'result');
}
function createDeadline(timeoutMs, controller) {
    let expired = false;
    let timer = null;
    const promise = new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
            expired = true;
            try {
                controller?.abort();
            }
            catch {
                /* noop */
            }
            reject(new Error('Request timed out'));
        }, timeoutMs);
    });
    return {
        promise,
        cancel: () => {
            if (timer !== null)
                clearTimeout(timer);
        },
        didTimeOut: () => expired
    };
}
function normalizeLookupError(err, timedOut) {
    if (timedOut)
        return new Error('Request timed out');
    if (err?.name === 'AbortError')
        return new Error('Request timed out');
    if (err instanceof Error)
        return err;
    return new Error(Utils.toSafeString(err, 'Unknown error'));
}
/**
 * Returns true when the given Content-Type header value represents
 * `application/octet-stream`, ignoring case and any media-type parameters
 * (e.g. `; charset=utf-8`).
 */
function isOctetStream(contentType) {
    if (typeof contentType !== 'string')
        return false;
    const baseType = contentType.split(';', 1)[0].trim().toLowerCase();
    return baseType === 'application/octet-stream';
}
export class HTTPSOverlayLookupFacilitator {
    fetchClient;
    allowHTTP;
    constructor(httpClient = defaultFetch, allowHTTP = false) {
        if (typeof httpClient !== 'function') {
            throw new TypeError('HTTPSOverlayLookupFacilitator requires a fetch implementation. ' +
                'In environments without fetch, provide a polyfill or custom implementation.');
        }
        this.fetchClient = httpClient;
        this.allowHTTP = allowHTTP;
    }
    async lookup(url, question, timeout = 2000) {
        if (!url.startsWith('https:') && !this.allowHTTP) {
            throw new Error('HTTPS facilitator can only use URLs that start with "https:"');
        }
        const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
        const deadline = createDeadline(timeout, controller);
        // Hard wall-clock deadline: in some environments (e.g. browser/Electron CORS
        // failures) the underlying fetch can stall without ever settling, and the
        // AbortController signal alone is insufficient to make the returned promise
        // resolve or reject. Race the fetch against a setTimeout-backed reject so
        // the consumer-facing promise always settles within `timeout` ms.
        const fetchPromise = this.performLookupRequest(url, question, controller?.signal);
        // Swallow background rejection if the deadline wins first.
        fetchPromise.catch(() => {
            /* noop */
        });
        try {
            return await Promise.race([fetchPromise, deadline.promise]);
        }
        catch (e) {
            throw normalizeLookupError(e, deadline.didTimeOut());
        }
        finally {
            deadline.cancel();
        }
    }
    async performLookupRequest(url, question, signal) {
        const fco = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Aggregation': 'yes'
            },
            body: JSON.stringify({ service: question.service, query: question.query }),
            signal
        };
        const response = await this.fetchClient(`${url}/lookup`, fco);
        if (!response.ok) {
            // 408/429 are availability/backpressure signals. Other 4xx responses
            // reject this request but do not prove that the host is unavailable, so
            // they remain distinguishable and neutral for availability reputation.
            const kind = response.status < 400 ||
                response.status === 408 ||
                response.status === 425 ||
                response.status === 429 ||
                response.status >= 500
                ? 'availability'
                : 'semantic';
            throw new LookupHTTPError(response.status, kind, response.statusText);
        }
        if (isOctetStream(response.headers.get('content-type'))) {
            return await this.parseOctetStreamLookup(response);
        }
        return await response.json();
    }
    /** Parse the aggregated octet-stream lookup response into an output-list LookupAnswer. */
    async parseOctetStreamLookup(response) {
        const payload = await response.arrayBuffer();
        const r = new Utils.Reader([...new Uint8Array(payload)]);
        const nOutpoints = r.readVarIntNum();
        const outpoints = [];
        for (let i = 0; i < nOutpoints; i++) {
            const txid = Utils.toHex(r.read(32));
            const outputIndex = r.readVarIntNum();
            const contextLength = r.readVarIntNum();
            const context = contextLength > 0 ? r.read(contextLength) : undefined;
            outpoints.push({ txid, outputIndex, context });
        }
        const beef = r.read();
        const beefObj = Beef.fromBinary(beef);
        const outputs = await this.extractAtomicOutputs(outpoints, beefObj);
        return { type: 'output-list', outputs };
    }
    /** Memoize per-txid atomic BEEF extraction, yielding to the event loop between outputs. */
    async extractAtomicOutputs(outpoints, beefObj) {
        const beefByTxid = new Map();
        const outputs = Array.from({ length: outpoints.length });
        for (let idx = 0; idx < outpoints.length; idx++) {
            const x = outpoints[idx];
            let beefBytes = beefByTxid.get(x.txid);
            if (beefBytes === undefined) {
                beefBytes = beefObj.toBinaryAtomic(x.txid);
                beefByTxid.set(x.txid, beefBytes);
            }
            outputs[idx] = {
                outputIndex: x.outputIndex,
                context: x.context,
                beef: beefBytes,
                txid: x.txid
            };
            // Yield to event loop so UI animations and other JS don't starve.
            if (idx > 0 && idx < outpoints.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
        return outputs;
    }
}
class LookupQuerySession {
    startedAt = Date.now();
    hostCount;
    correlationId;
    completedHosts = 0;
    successfulHosts = 0;
    emptyHosts = 0;
    failedHosts = 0;
    rejectedHosts = 0;
    freeformHosts = 0;
    emittedFinal = false;
    graceMs;
    softTimeoutMs;
    waitForAllHosts;
    resolveTxId;
    outputsMap = new Map();
    txIds = [];
    queue = [];
    waiter = null;
    firstResponseAt = null;
    graceTimer = null;
    softTimer = null;
    graceFired = false;
    emittedOnce = false;
    constructor(options) {
        this.hostCount = options.hostCount;
        this.graceMs = options.graceMs;
        this.softTimeoutMs = options.softTimeoutMs;
        this.waitForAllHosts = options.waitForAllHosts;
        this.correlationId = options.correlationId;
        this.resolveTxId = options.resolveTxId;
    }
    push(event) {
        this.queue.push(event);
        if (this.waiter === null)
            return;
        const waiter = this.waiter;
        this.waiter = null;
        waiter();
    }
    recordOutputAnswer(answer) {
        this.successfulHosts++;
        if (answer.outputs.length === 0) {
            this.emptyHosts++;
            return;
        }
        this.push({ kind: 'answer', answer });
    }
    recordFreeformAnswer() {
        this.freeformHosts++;
    }
    recordRejection() {
        this.rejectedHosts++;
    }
    recordAvailabilityFailure() {
        this.failedHosts++;
    }
    recordDone() {
        this.completedHosts++;
        this.push({ kind: 'done' });
    }
    mergeAnswer(answer) {
        let added = false;
        const now = Date.now();
        for (const output of answer.outputs) {
            const txId = this.resolveTxId(output, now);
            if (txId === null)
                continue;
            const key = `${txId}.${output.outputIndex}`;
            if (this.outputsMap.has(key))
                continue;
            this.outputsMap.set(key, output);
            this.txIds.push(txId);
            added = true;
        }
        return added;
    }
    snapshot(isFinal) {
        return {
            type: 'output-list',
            outputs: Array.from(this.outputsMap.values()),
            txIds: this.txIds.slice(),
            isFinal,
            hostCount: this.hostCount,
            completedHosts: this.completedHosts,
            successfulHosts: this.successfulHosts,
            emptyHosts: this.emptyHosts,
            failedHosts: this.failedHosts,
            rejectedHosts: this.rejectedHosts,
            freeformHosts: this.freeformHosts,
            ...(this.correlationId !== undefined
                ? { correlationId: this.correlationId }
                : {})
        };
    }
    handleAnswer(answer) {
        const added = this.mergeAnswer(answer);
        if (this.firstResponseAt === null) {
            this.firstResponseAt = Date.now();
            if (!this.graceFired && this.graceMs > 0) {
                this.graceTimer = setTimeout(() => {
                    this.graceFired = true;
                    this.push({ kind: 'grace' });
                }, this.graceMs);
            }
            else {
                this.graceFired = true;
            }
        }
        if (this.graceFired &&
            added &&
            (this.emittedOnce || !this.waitForAllHosts)) {
            this.emittedOnce = true;
            return this.snapshot(false);
        }
        return null;
    }
    handleGrace() {
        if (this.emittedOnce || this.waitForAllHosts)
            return null;
        this.emittedOnce = true;
        return this.snapshot(false);
    }
    handleSoft() {
        let snapshot = null;
        if (!this.emittedOnce) {
            this.graceFired = true;
            this.emittedOnce = true;
            snapshot = this.snapshot(false);
        }
        return {
            snapshot,
            stop: typeof this.softTimeoutMs === 'number' &&
                this.firstResponseAt !== null
        };
    }
    async nextEvent() {
        if (this.queue.length === 0) {
            await new Promise(resolve => {
                this.waiter = resolve;
            });
        }
        return this.queue.shift();
    }
    processEvent(event) {
        switch (event.kind) {
            case 'answer':
                return { snapshot: this.handleAnswer(event.answer), stop: false };
            case 'grace':
                return { snapshot: this.handleGrace(), stop: false };
            case 'soft':
                return this.handleSoft();
            case 'done':
                return { snapshot: null, stop: false };
        }
    }
    async *progress() {
        if (typeof this.softTimeoutMs === 'number' &&
            this.softTimeoutMs >= 0) {
            this.softTimer = setTimeout(() => this.push({ kind: 'soft' }), this.softTimeoutMs);
        }
        try {
            let stop = false;
            while (this.completedHosts < this.hostCount && !stop) {
                const event = await this.nextEvent();
                const outcome = this.processEvent(event);
                if (outcome.snapshot != null)
                    yield outcome.snapshot;
                stop = outcome.stop;
            }
            const finalSnapshot = this.snapshot(true);
            this.emittedFinal = true;
            yield finalSnapshot;
        }
        finally {
            if (this.graceTimer !== null)
                clearTimeout(this.graceTimer);
            if (this.softTimer !== null)
                clearTimeout(this.softTimer);
        }
    }
}
/**
 * Represents a Lookup Resolver.
 */
export default class LookupResolver {
    facilitator;
    slapTrackers;
    hostOverrides;
    additionalHosts;
    networkPreset;
    hostReputation;
    telemetry;
    // ---- Caches / memoization ----
    hostsCache;
    hostsInFlight;
    hostsTtlMs;
    hostsMaxEntries;
    txMemo;
    txMemoTtlMs;
    /**
     * Records which SLAP tracker most recently advertised each host. Used to
     * attach `advertisedBy` to onUnreachableHost callbacks so downstream
     * notification consumers know which tracker has a stale advertisement.
     */
    advertisedBy;
    lastUnreachableNotificationAt;
    constructor(config = {}) {
        this.networkPreset = config.networkPreset ?? 'mainnet';
        this.facilitator =
            config.facilitator ??
                new HTTPSOverlayLookupFacilitator(undefined, this.networkPreset === 'local');
        this.slapTrackers = config.slapTrackers ?? this.defaultSlapTrackers();
        const hostOverrides = config.hostOverrides ?? {};
        this.assertValidOverrideServices(hostOverrides);
        this.hostOverrides = hostOverrides;
        this.additionalHosts = config.additionalHosts ?? {};
        this.telemetry = new Telemetry(config.telemetry);
        const rs = config.reputationStorage;
        if (rs === 'localStorage') {
            this.hostReputation = new HostReputationTracker();
        }
        else if (typeof rs === 'object' &&
            rs !== null &&
            typeof rs.get === 'function' &&
            typeof rs.set === 'function') {
            this.hostReputation = new HostReputationTracker(rs);
        }
        else {
            this.hostReputation = getOverlayHostReputationTracker();
        }
        // cache tuning
        this.hostsTtlMs = config.cache?.hostsTtlMs ?? 5 * 60 * 1000; // 5 min
        this.hostsMaxEntries = config.cache?.hostsMaxEntries ?? 128;
        this.txMemoTtlMs = config.cache?.txMemoTtlMs ?? 10 * 60 * 1000; // 10 min
        this.hostsCache = new Map();
        this.hostsInFlight = new Map();
        this.txMemo = new Map();
        this.advertisedBy = new Map();
        this.lastUnreachableNotificationAt = new Map();
    }
    defaultSlapTrackers() {
        switch (this.networkPreset) {
            case 'mainnet':
                return DEFAULT_SLAP_TRACKERS;
            case 'testnet':
                return DEFAULT_TESTNET_SLAP_TRACKERS;
            case 'teratestnet':
                return DEFAULT_TTN_SLAP_TRACKERS;
            case 'local':
                return [];
        }
    }
    /**
     * Given a LookupQuestion, returns a LookupAnswer. Aggregates across multiple services and supports resiliency.
     *
     * Optional `options.graceMs` overrides the per-call grace window (default 80 ms).
     * Optional `options.softTimeoutMs` resolves the query early with whatever has arrived once any host has
     * answered (or with an empty result if no host has answered by `softTimeoutMs`).
     */
    async query(question, timeout, options) {
        return (await this.queryDetailed(question, timeout, options)).answer;
    }
    /**
     * Performs a lookup and returns both its answer and the host settlement
     * evidence required by security-sensitive consumers to distinguish an
     * authoritative empty result from an availability failure.
     */
    async queryDetailed(question, timeout, options) {
        // A generic resolver cannot prove that a larger answer is fresher or more
        // authoritative. The blocking API therefore waits for every bounded host
        // settlement by default and merges the valid outputs. Callers that prefer
        // first-response latency can opt out with waitForAllHosts: false; callers
        // wanting progressive enrichment use query$().
        // Take only the first emission, then explicitly close the iterator so the
        // generator's `finally` block runs and clears any outstanding timers.
        const iter = this.query$(question, timeout, {
            ...options,
            waitForAllHosts: options?.waitForAllHosts ?? options?.holdForUnknownHosts ?? true
        })[Symbol.asyncIterator]();
        let last = null;
        try {
            const { value, done } = await iter.next();
            if (done !== true && value != null)
                last = value;
        }
        finally {
            await iter.return?.(undefined);
        }
        return {
            answer: {
                type: 'output-list',
                outputs: last?.outputs ?? []
            },
            progress: last ?? {
                type: 'output-list',
                outputs: [],
                txIds: [],
                isFinal: true,
                hostCount: 0,
                completedHosts: 0,
                successfulHosts: 0,
                emptyHosts: 0,
                failedHosts: 0,
                rejectedHosts: 0,
                freeformHosts: 0,
                ...(options?.correlationId !== undefined ? { correlationId: options.correlationId } : {})
            }
        };
    }
    appendAdditionalHosts(service, hosts) {
        const additional = this.additionalHosts[service];
        if (additional == null || additional.length === 0)
            return;
        const seen = new Set(hosts);
        for (const host of additional) {
            if (!seen.has(host))
                hosts.push(host);
        }
    }
    async competentHostsFor(question) {
        let hosts;
        if (question.service === 'ls_slap') {
            hosts =
                this.networkPreset === 'local'
                    ? ['http://localhost:8080']
                    : this.slapTrackers;
        }
        else if (this.hostOverrides[question.service] != null) {
            hosts = this.hostOverrides[question.service];
        }
        else if (this.networkPreset === 'local') {
            hosts = ['http://localhost:8080'];
        }
        else {
            hosts = await this.getCompetentHostsCached(question.service);
        }
        this.appendAdditionalHosts(question.service, hosts);
        if (hosts.length < 1) {
            throw new Error(`No competent ${this.networkPreset} hosts found by the SLAP trackers for lookup service: ${question.service}`);
        }
        return hosts;
    }
    isSlapRecoveryEligible(service) {
        return (service !== 'ls_slap' &&
            this.hostOverrides[service] == null &&
            this.networkPreset !== 'local');
    }
    async rankedHostsFor(question) {
        const competentHosts = await this.competentHostsFor(question);
        let rankedHosts;
        try {
            rankedHosts = this.prepareHostsForQuery(competentHosts, `lookup service ${question.service}`);
        }
        catch (error) {
            if (!this.isSlapRecoveryEligible(question.service))
                throw error;
            this.hostsCache.delete(question.service);
            const fresh = await this.refreshHosts(question.service, true);
            this.appendAdditionalHosts(question.service, fresh);
            if (fresh.length < 1) {
                throw new Error(`No competent ${this.networkPreset} hosts found by the SLAP trackers for lookup service: ${question.service}`);
            }
            rankedHosts = this.prepareHostsForQuery(fresh, `lookup service ${question.service}`);
        }
        if (rankedHosts.length < 1) {
            throw new Error(`All competent hosts for ${question.service} are temporarily unavailable due to backoff.`);
        }
        return rankedHosts;
    }
    unreachableNotificationCooldown(options) {
        const requested = options?.unreachableHostNotificationCooldownMs;
        return typeof requested === 'number' &&
            Number.isFinite(requested) &&
            requested >= 0
            ? requested
            : DEFAULT_UNREACHABLE_NOTIFICATION_COOLDOWN_MS;
    }
    notifyUnreachableHost(host, service, error, callback, cooldownMs) {
        if (typeof callback !== 'function')
            return;
        const notificationKey = `${service}\u0000${host}`;
        const now = Date.now();
        const lastNotificationAt = this.lastUnreachableNotificationAt.get(notificationKey) ??
            Number.NEGATIVE_INFINITY;
        if (now - lastNotificationAt < cooldownMs)
            return;
        if (this.lastUnreachableNotificationAt.size >=
            MAX_NOTIFICATION_DEDUP_ENTRIES) {
            this.evictOldest(this.lastUnreachableNotificationAt);
        }
        this.lastUnreachableNotificationAt.set(notificationKey, now);
        try {
            const result = callback({
                host,
                service,
                error: lookupErrorMessage(error),
                advertisedBy: this.advertisedBy.get(host)
            });
            void Promise.resolve(result).catch(() => {
                /* consumer callback is isolated */
            });
        }
        catch {
            /* never let a consumer callback break the query */
        }
    }
    recordLookupHostAnswer(session, service, host, answer, hostStartedAt, correlationId) {
        if (isOutputListAnswer(answer)) {
            session.recordOutputAnswer(answer);
            this.captureHostTelemetry(service, host, answer.outputs.length === 0 ? 'empty' : 'success', Date.now() - hostStartedAt, correlationId);
            return;
        }
        session.recordFreeformAnswer();
        this.captureHostTelemetry(service, host, 'freeform', Date.now() - hostStartedAt, correlationId);
    }
    recordLookupHostFailure(context, error) {
        const { session, service, host, hostStartedAt, correlationId, onUnreachableHost, notificationCooldownMs } = context;
        const semanticRejection = isSemanticLookupRejection(error);
        if (semanticRejection)
            session.recordRejection();
        else
            session.recordAvailabilityFailure();
        this.captureHostTelemetry(service, host, semanticRejection ? 'rejected' : 'failed', Date.now() - hostStartedAt, correlationId, error);
        if (!semanticRejection) {
            this.notifyUnreachableHost(host, service, error, onUnreachableHost, notificationCooldownMs);
        }
    }
    startLookupHostQueries(hosts, question, timeout, session, options) {
        const correlationId = session.correlationId;
        const notificationCooldownMs = this.unreachableNotificationCooldown(options);
        for (const host of hosts) {
            const hostStartedAt = Date.now();
            void this.lookupHostWithTracking(host, question, timeout)
                .then(answer => {
                this.recordLookupHostAnswer(session, question.service, host, answer, hostStartedAt, correlationId);
            })
                .catch(error => {
                this.recordLookupHostFailure({
                    session,
                    service: question.service,
                    host,
                    hostStartedAt,
                    correlationId,
                    onUnreachableHost: options?.onUnreachableHost,
                    notificationCooldownMs
                }, error);
            })
                .finally(() => {
                session.recordDone();
            });
        }
    }
    /**
     * Iterable form of {@link query}. Emits partial results as hosts answer.
     *
     * Emission order:
     *  - First emission: after the grace window expires (or as soon as the soft timeout elapses), containing
     *    every output gathered from hosts that answered by then.
     *  - Subsequent emissions: re-emitted whenever a late host returns extra outputs that weren't in earlier
     *    emissions. Each emission contains the cumulative `outputs` set.
     *  - Final emission: `isFinal: true` once all in-flight hosts have settled (success / fail / timeout). The
     *    caller can `break` early; outstanding work is bounded by the per-host timeout.
     *
     * No host work runs past its per-host `timeout` — there is no leak risk on early break.
     */
    async *query$(question, timeout, options) {
        const rankedHosts = await this.rankedHostsFor(question);
        const hostCount = rankedHosts.length;
        const correlationId = options?.correlationId ??
            (this.telemetry.enabled ? this.telemetry.createCorrelationId() : undefined);
        const session = new LookupQuerySession({
            hostCount,
            graceMs: options?.graceMs ?? 80,
            softTimeoutMs: options?.softTimeoutMs,
            waitForAllHosts: options?.waitForAllHosts ?? options?.holdForUnknownHosts ?? false,
            correlationId,
            resolveTxId: (output, now) => this.resolveTxIdForOutput(output, now)
        });
        this.telemetry.capture({
            name: 'sdk.overlay.lookup.started',
            component: 'sdk.lookup-resolver',
            severity: 'debug',
            correlationId,
            attributes: {
                service: question.service,
                network: this.networkPreset,
                hostCount
            }
        });
        this.startLookupHostQueries(rankedHosts, question, timeout, session, options);
        try {
            for await (const progress of session.progress()) {
                if (progress.isFinal) {
                    this.captureLookupCompletedTelemetry(question.service, progress, Date.now() - session.startedAt);
                }
                yield progress;
            }
        }
        finally {
            if (!session.emittedFinal) {
                this.telemetry.capture({
                    name: 'sdk.overlay.lookup.cancelled',
                    component: 'sdk.lookup-resolver',
                    severity: 'debug',
                    correlationId,
                    attributes: {
                        service: question.service,
                        hostCount,
                        completedHosts: session.completedHosts,
                        durationMs: Date.now() - session.startedAt
                    }
                });
            }
        }
    }
    /**
     * Cached wrapper for competent host discovery with stale-while-revalidate.
     */
    async getCompetentHostsCached(service) {
        const now = Date.now();
        const cached = this.hostsCache.get(service);
        // if fresh, return immediately
        if (typeof cached === 'object' && cached.expiresAt > now) {
            return cached.hosts.slice();
        }
        // if stale but present, kick off a refresh if not already in-flight and return stale
        if (typeof cached === 'object' && cached.expiresAt <= now) {
            if (!this.hostsInFlight.has(service)) {
                this.hostsInFlight.set(service, this.refreshHosts(service).finally(() => {
                    this.hostsInFlight.delete(service);
                }));
            }
            return cached.hosts.slice();
        }
        // no cache: coalesce concurrent requests
        if (this.hostsInFlight.has(service)) {
            try {
                const hosts = await this.hostsInFlight.get(service);
                if (typeof hosts !== 'object') {
                    throw new TypeError('Hosts is not defined.');
                }
                return hosts.slice();
            }
            catch {
                // fall through to a fresh attempt below
            }
        }
        const promise = this.refreshHosts(service).finally(() => {
            this.hostsInFlight.delete(service);
        });
        this.hostsInFlight.set(service, promise);
        const hosts = await promise;
        return hosts.slice();
    }
    /**
     * Actually resolves competent hosts from SLAP trackers and updates cache.
     */
    async refreshHosts(service, requireAvailable = false) {
        const hosts = await this.findCompetentHosts(service, requireAvailable);
        const expiresAt = Date.now() + this.hostsTtlMs;
        // bounded cache with simple FIFO eviction
        if (!this.hostsCache.has(service) && this.hostsCache.size >= this.hostsMaxEntries) {
            const oldestKey = this.hostsCache.keys().next().value;
            if (oldestKey !== undefined)
                this.hostsCache.delete(oldestKey);
        }
        this.hostsCache.set(service, { hosts, expiresAt });
        return hosts;
    }
    /**
     * Extracts competent host domains from a SLAP tracker response.
     */
    extractHostsFromAnswer(answer, service) {
        const hosts = [];
        if (answer.type !== 'output-list')
            return hosts;
        for (const output of answer.outputs) {
            try {
                const tx = Transaction.fromBEEF(output.beef);
                const script = tx.outputs[output.outputIndex]?.lockingScript;
                if (typeof script !== 'object' || script === null)
                    continue;
                const parsed = OverlayAdminTokenTemplate.decode(script);
                if (parsed.topicOrService !== service || parsed.protocol !== 'SLAP')
                    continue;
                if (typeof parsed.domain === 'string' && parsed.domain.length > 0) {
                    hosts.push(parsed.domain);
                }
            }
            catch {
                continue;
            }
        }
        return hosts;
    }
    /**
     * Returns a list of competent hosts for a given lookup service.
     * Resolves as soon as the first SLAP tracker responds with valid hosts.
     * Remaining trackers continue in the background for reputation tracking.
     * @param service Service for which competent hosts are to be returned
     * @returns Array of hosts competent for resolving queries
     */
    async findCompetentHosts(service, requireAvailable = false) {
        const query = {
            service: 'ls_slap',
            query: { service }
        };
        const trackerHosts = this.prepareHostsForQuery(this.slapTrackers, 'SLAP trackers');
        if (trackerHosts.length === 0)
            return [];
        // Fire all trackers, resolve as soon as any returns valid hosts.
        // Remaining trackers continue in the background for reputation tracking.
        return await new Promise(resolve => {
            const allHosts = new Set();
            let resolved = false;
            let pending = trackerHosts.length;
            for (const tracker of trackerHosts) {
                this.lookupHostWithTracking(tracker, query, MAX_TRACKER_WAIT_TIME)
                    .then(answer => {
                    const hosts = isOutputListAnswer(answer)
                        ? this.extractHostsFromAnswer(answer, service)
                        : [];
                    for (const h of hosts) {
                        if (!allHosts.has(h)) {
                            allHosts.add(h);
                            // First-seen attribution: the tracker that surfaced this host
                            // gets credit, used by onUnreachableHost callbacks.
                            this.advertisedBy.set(h, tracker);
                        }
                    }
                    const now = Date.now();
                    const foundAvailable = [...allHosts].some(host => {
                        const backoffUntil = this.hostReputation.snapshot(host)?.backoffUntil ?? 0;
                        return backoffUntil <= now;
                    });
                    if (!resolved && allHosts.size > 0 && (!requireAvailable || foundAvailable)) {
                        resolved = true;
                        resolve([...allHosts]);
                    }
                })
                    .catch(() => {
                    /* tracker failure tracked in reputation */
                })
                    .finally(() => {
                    pending--;
                    if (pending === 0 && !resolved) {
                        resolved = true;
                        resolve([...allHosts]);
                    }
                });
            }
        });
    }
    /**
     * Resolve a txid for an aggregated lookup output. Uses the threaded-through `output.txid`
     * fast path when present; otherwise memoizes Transaction.fromBEEF(beef).id('hex') keyed by
     * the BEEF byte sequence. Returns null when the BEEF is unparseable.
     */
    resolveTxIdForOutput(output, now) {
        if (typeof output.txid === 'string' && output.txid.length > 0) {
            return output.txid;
        }
        const keyForBeef = Array.isArray(output.beef) ? output.beef.join(',') : '';
        const memo = this.txMemo.get(keyForBeef);
        if (typeof memo === 'object' && memo !== null && memo.expiresAt > now) {
            return memo.txId;
        }
        try {
            const txId = Transaction.fromBEEF(output.beef).id('hex');
            if (this.txMemo.size > 4096)
                this.evictOldest(this.txMemo);
            this.txMemo.set(keyForBeef, { txId, expiresAt: now + this.txMemoTtlMs });
            return txId;
        }
        catch {
            return null;
        }
    }
    /** Evict an arbitrary "oldest" entry from a Map (iteration order). */
    evictOldest(m) {
        const firstKey = m.keys().next().value;
        if (firstKey !== undefined)
            m.delete(firstKey);
    }
    assertValidOverrideServices(overrides) {
        for (const service of Object.keys(overrides)) {
            if (!service.startsWith('ls_')) {
                throw new Error(`Host override service names must start with "ls_": ${service}`);
            }
        }
    }
    prepareHostsForQuery(hosts, context) {
        if (hosts.length === 0)
            return [];
        const now = Date.now();
        const ranked = this.hostReputation.rankHosts(hosts, now);
        const available = ranked.filter(h => h.backoffUntil <= now).map(h => h.host);
        if (available.length > 0)
            return available;
        const soonest = Math.min(...ranked.map(h => h.backoffUntil));
        const waitMs = Math.max(soonest - now, 0);
        throw new Error(`All ${context} hosts are backing off for approximately ${waitMs}ms due to repeated failures.`);
    }
    async lookupHostWithTracking(host, question, timeout) {
        const startedAt = Date.now();
        const effectiveTimeout = typeof timeout === 'number' && Number.isFinite(timeout) && timeout >= 0
            ? timeout
            : DEFAULT_LOOKUP_TIMEOUT;
        const deadline = createDeadline(effectiveTimeout);
        // Start the custom facilitator in a promise chain so synchronous throws
        // become rejections governed by the same wall-clock deadline.
        const lookupPromise = Promise.resolve().then(() => this.facilitator.lookup(host, question, timeout));
        lookupPromise.catch(() => {
            /* deadline may win while custom facilitator settles later */
        });
        let answer;
        try {
            answer = await Promise.race([lookupPromise, deadline.promise]);
        }
        catch (err) {
            const normalized = normalizeLookupError(err, deadline.didTimeOut());
            if (!isSemanticLookupRejection(err))
                this.hostReputation.recordFailure(host, normalized);
            throw isSemanticLookupRejection(err) ? err : normalized;
        }
        finally {
            deadline.cancel();
        }
        if (isOutputListAnswer(answer)) {
            this.hostReputation.recordSuccess(host, Date.now() - startedAt);
            return answer;
        }
        // A valid freeform response is neutral: it proves this request reached the
        // service, but it must not erase an availability backoff established by a
        // concurrent failing request and cannot contribute to output aggregation.
        if (isFreeformAnswer(answer))
            return answer;
        const malformed = new Error('Malformed lookup response');
        this.hostReputation.recordFailure(host, malformed);
        throw malformed;
    }
    captureHostTelemetry(service, host, outcome, durationMs, correlationId, error) {
        let hostOrigin = 'invalid-host';
        try {
            hostOrigin = new URL(host).origin;
        }
        catch {
            // Host input is consumer configuration; never forward paths or query data.
        }
        this.telemetry.capture({
            name: 'sdk.overlay.lookup.host-settled',
            component: 'sdk.lookup-resolver',
            severity: outcome === 'failed' ? 'warn' : 'debug',
            correlationId,
            attributes: {
                service,
                hostOrigin,
                outcome,
                durationMs
            },
            error
        });
    }
    captureLookupCompletedTelemetry(service, progress, durationMs) {
        const degraded = progress.failedHosts > 0 || progress.rejectedHosts > 0 || progress.freeformHosts > 0;
        this.telemetry.capture({
            name: 'sdk.overlay.lookup.completed',
            component: 'sdk.lookup-resolver',
            severity: degraded ? 'warn' : 'info',
            correlationId: progress.correlationId,
            attributes: {
                service,
                durationMs,
                hostCount: progress.hostCount,
                completedHosts: progress.completedHosts,
                successfulHosts: progress.successfulHosts,
                emptyHosts: progress.emptyHosts,
                failedHosts: progress.failedHosts,
                rejectedHosts: progress.rejectedHosts,
                freeformHosts: progress.freeformHosts,
                outputCount: progress.outputs.length,
                isFinal: progress.isFinal
            }
        });
    }
}
//# sourceMappingURL=LookupResolver.js.map